// Client-side helpers for the Phase 3 structured tool surface.
//
// Mirrors a small, read-only subset of `supabase/functions/_shared/writer-assemble.ts`
// so the WriterProject sidebar can render a live paper map and pre-compile
// diagnostics without a round-trip to an edge function. Kept intentionally
// pure: given the three structured tables' rows, produce a `PaperMap` and a
// `ValidationDiagnostic[]`.

import { supabase } from '@/integrations/supabase/client';

export interface SectionRow {
  anchor_id: string;
  label: string;
  title: string;
  order_index: number;
  body: string;
}
export interface PreambleRow {
  field: string;
  value: string;
}
export interface BibEntryRow {
  citekey: string;
  bibtex: string;
}

export interface PaperMapEntry {
  anchor_id: string;
  label: string;
  title: string;
  order_index: number;
  byte_size: number;
  refs_used: string[];
  citekeys_used: string[];
}

export interface PaperMap {
  preamble: PreambleRow[];
  sections: PaperMapEntry[];
  bib: Array<{ citekey: string; short_title: string; uncited: boolean }>;
  uncited_bib_count: number;
}

export interface ValidationDiagnostic {
  code:
    | 'missing_label'
    | 'unresolved_ref'
    | 'unresolved_cite'
    | 'uncited_bib_entry'
    | 'duplicate_anchor'
    | 'duplicate_label';
  message: string;
  anchor_id?: string;
  target?: string;
}

const REF_RE = /\\(?:ref|eqref|autoref|cref|Cref|pageref)\{([^}]+)\}/g;
const CITE_RE = /\\cite[a-zA-Z*]*\{([^}]+)\}/g;
const LABEL_RE = /\\label\{([^}]+)\}/g;


function matchAll(src: string, re: RegExp): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  const r = new RegExp(re.source, re.flags);
  while ((m = r.exec(src)) !== null) out.push(m[1]);
  return out;
}
function uniq<T>(xs: T[]): T[] {
  return Array.from(new Set(xs));
}

export function buildPaperMap(input: {
  sections: SectionRow[];
  preamble: PreambleRow[];
  bibEntries: BibEntryRow[];
}): PaperMap {
  const sections = [...input.sections]
    .sort((a, b) => a.order_index - b.order_index)
    .map((s): PaperMapEntry => ({
      anchor_id: s.anchor_id,
      label: s.label,
      title: s.title,
      order_index: s.order_index,
      byte_size: (s.body ?? '').length,
      refs_used: uniq(matchAll(s.body ?? '', REF_RE)),
      citekeys_used: uniq(
        matchAll(s.body ?? '', CITE_RE).flatMap((k) =>
          k.split(',').map((x) => x.trim()).filter(Boolean),
        ),
      ),
    }));
  const cited = new Set(sections.flatMap((section) => section.citekeys_used));
  const bib = input.bibEntries.map((entry) => ({
    citekey: entry.citekey,
    short_title: entry.bibtex.match(/title\s*=\s*[{\"]([^\"}]{1,200})/i)?.[1]?.trim() ?? '',
    uncited: !cited.has(entry.citekey),
  }));
  return {
    preamble: input.preamble,
    sections,
    bib,
    uncited_bib_count: bib.filter((entry) => entry.uncited).length,
  };
}

export function validate(input: {
  sections: SectionRow[];
  bibEntries: BibEntryRow[];
}): ValidationDiagnostic[] {
  const out: ValidationDiagnostic[] = [];
  const anchors = new Set<string>();
  const labels = new Set<string>();
  const bibKeys = new Set(input.bibEntries.map((e) => e.citekey));
  const citedKeys = new Set<string>();

  for (const s of input.sections) {
    if (anchors.has(s.anchor_id)) {
      out.push({
        code: 'duplicate_anchor',
        message: `Duplicate section anchor: ${s.anchor_id}`,
        anchor_id: s.anchor_id,
      });
    }
    if (s.label && labels.has(s.label)) {
      out.push({
        code: 'duplicate_label',
        message: `Duplicate section label: ${s.label}`,
        anchor_id: s.anchor_id,
        target: s.label,
      });
    }
    anchors.add(s.anchor_id);
    if (s.label) labels.add(s.label);
    if (!s.label) {
      out.push({
        code: 'missing_label',
        message: `Section “${s.title || s.anchor_id}” has no \\label{}`,
        anchor_id: s.anchor_id,
      });
    }
    // Collect inline labels (figures, tables, equations, etc.) so \ref
    // targets defined inside a section body resolve without a section label.
    for (const inline of matchAll(s.body ?? '', LABEL_RE)) {
      labels.add(inline.trim());
    }
  }


  for (const s of input.sections) {
    for (const target of uniq(matchAll(s.body ?? '', REF_RE))) {
      if (!labels.has(target)) {
        out.push({
          code: 'unresolved_ref',
          message: `\\ref{${target}} in “${s.title || s.anchor_id}” does not resolve`,
          anchor_id: s.anchor_id,
          target,
        });
      }
    }
    for (const raw of matchAll(s.body ?? '', CITE_RE)) {
      for (const k of raw.split(',').map((x) => x.trim()).filter(Boolean)) {
        citedKeys.add(k);
        if (!bibKeys.has(k)) {
          out.push({
            code: 'unresolved_cite',
            message: `\\cite{${k}} in “${s.title || s.anchor_id}” does not resolve`,
            anchor_id: s.anchor_id,
            target: k,
          });
        }
      }
    }
  }
  for (const key of bibKeys) {
    if (!citedKeys.has(key)) {
      out.push({
        code: 'uncited_bib_entry',
        message: `Bibliography entry ${key} is registered but not cited in the text.`,
        target: key,
      });
    }
  }
  return out;
}

export interface StructuredSnapshot {
  sections: SectionRow[];
  preamble: PreambleRow[];
  bibEntries: BibEntryRow[];
}

export async function loadStructuredSnapshot(
  projectId: string,
): Promise<StructuredSnapshot> {
  const [sectionsRes, preambleRes, bibRes] = await Promise.all([
    supabase
      .from('writer_project_sections')
      .select('anchor_id, label, title, order_index, body')
      .eq('project_id', projectId)
      .order('order_index', { ascending: true }),
    supabase
      .from('writer_project_preamble')
      .select('field, value')
      .eq('project_id', projectId),
    supabase
      .from('writer_bib_entries')
      .select('citekey, bibtex')
      .eq('project_id', projectId),
  ]);
  return {
    sections: (sectionsRes.data ?? []) as SectionRow[],
    preamble: (preambleRes.data ?? []) as PreambleRow[],
    bibEntries: (bibRes.data ?? []) as BibEntryRow[],
  };
}
